import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { 
  Package, Users, ShoppingCart, TrendingUp, 
  Eye, Check, Truck, XCircle, Clock, RefreshCw,
  Search, ChevronDown, ChevronUp, Mail, DollarSign,
  UserCheck, UserX, AlertCircle, Calendar, Building2,
  Phone, MapPin, FileText, Send, MoreHorizontal,
  CheckCircle2, Ban, Undo2, Download, Filter,
  LayoutDashboard, UserCog, ClipboardList, Tag,
  Plus, Pause, Play, Trash2, Percent, Star, ThumbsUp, ThumbsDown
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['info@penlabpeptide.com', 'info@penpeptido.com'];

const AdminPage = () => {
  const { currentLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [userFilter, setUserFilter] = useState('all');
  const [orderFilter, setOrderFilter] = useState('all');
  const [affiliateFilter, setAffiliateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showShipModal, setShowShipModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('DHL Express');
  const [actionLoading, setActionLoading] = useState(false);
  
  // New promo code form state
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_percent: 10,
    max_uses: null,
    starts_at: '',
    expires_at: ''
  });
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingUsers: 0,
    verifiedUsers: 0,
    rejectedUsers: 0,
    totalOrders: 0,
    pendingOrders: 0,
    paidOrders: 0,
    shippedOrders: 0,
    totalRevenue: 0,
    pendingRevenue: 0,
    pendingReviews: 0
  });

  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [reviewLoading, setReviewLoading] = useState(false);

  const isAdmin = isAuthenticated && user && ADMIN_EMAILS.includes(user.email);

  // Load data
  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const [usersRes, ordersRes, promoRes, affiliatesRes, reviewsCountRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/orders'),
        api.get('/promo-codes').catch(() => ({ data: [] })),
        api.get('/admin/affiliates').catch(() => ({ data: [] })),
        api.get('/admin/reviews', { params: { status: 'pending', limit: 1 } }).catch(() => ({ data: { pending_total: 0 } }))
      ]);
      
      const usersData = usersRes.data || [];
      const ordersData = ordersRes.data || [];
      const promoData = promoRes.data || [];
      const affiliatesData = affiliatesRes.data || [];
      const pendingReviewsTotal = reviewsCountRes.data?.pending_total || 0;
      
      setUsers(usersData);
      setOrders(ordersData);
      setPromoCodes(promoData);
      setAffiliates(affiliatesData);
      
      // Calculate stats
      const paidOrders = ordersData.filter(o => o.payment_status === 'paid');
      const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const pendingRevenue = ordersData
        .filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.total || 0), 0);
      
      setStats({
        totalUsers: usersData.length,
        pendingUsers: usersData.filter(u => u.verification_status === 'under_review').length,
        verifiedUsers: usersData.filter(u => u.verification_status === 'verified').length,
        rejectedUsers: usersData.filter(u => u.verification_status === 'not_approved').length,
        totalOrders: ordersData.length,
        pendingOrders: ordersData.filter(o => ['pending', 'pending_kyc', 'pending_kyc_approval', 'kyc_approved', 'awaiting_payment', 'processing'].includes(o.status)).length,
        paidOrders: paidOrders.length,
        shippedOrders: ordersData.filter(o => o.status === 'shipped' || o.status === 'delivered').length,
        totalRevenue,
        pendingRevenue,
        pendingReviews: pendingReviewsTotal
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/admin');
      return;
    }
    if (isAdmin) {
      loadData();
    }
  }, [isAuthenticated, isAdmin, navigate, loadData]);

  // ===== Reviews moderation =====
  const loadReviews = useCallback(async (filter = reviewFilter) => {
    if (!isAdmin) return;
    try {
      setReviewLoading(true);
      const res = await api.get('/admin/reviews', { params: { status: filter, limit: 100 } });
      setReviews(res.data?.reviews || []);
      setStats(prev => ({ ...prev, pendingReviews: res.data?.pending_total || prev.pendingReviews }));
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setReviewLoading(false);
    }
  }, [isAdmin, reviewFilter]);

  useEffect(() => {
    if (activeTab === 'reviews' && isAdmin) {
      loadReviews(reviewFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewFilter, isAdmin]);

  const moderateReview = async (id, action) => {
    try {
      setActionLoading(true);
      await api.post(`/admin/reviews/${id}/${action}`, {});
      toast.success(`Review ${action}d`);
      await loadReviews(reviewFilter);
    } catch (e) {
      toast.error(`Failed to ${action} review`);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      setActionLoading(true);
      await api.delete(`/admin/reviews/${id}`);
      toast.success('Review deleted');
      await loadReviews(reviewFilter);
    } catch (e) {
      toast.error('Failed to delete review');
    } finally {
      setActionLoading(false);
    }
  };

  // User actions
  const updateUserStatus = async (userId, status) => {
    try {
      setActionLoading(true);
      await api.post(`/admin/users/${userId}/verify`, { status });
      await loadData();
      if (selectedUser?.user_id === userId) {
        setSelectedUser(prev => ({ ...prev, verification_status: status }));
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  // Order actions
  const updateOrderStatus = async (orderId, status) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/orders/${orderId}/status`, { status });
      await loadData();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status }));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    } finally {
      setActionLoading(false);
    }
  };

  const markPaymentReceived = async (orderId) => {
    try {
      setActionLoading(true);
      const order = orders.find(o => o.id === orderId);
      await api.post(`/admin/orders/${orderId}/payment-received`, {
        order_id: orderId,
        amount: order?.total || 0,
        payment_date: new Date().toISOString()
      });
      await loadData();
      alert('Payment confirmed and notification sent!');
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('Failed to confirm payment');
    } finally {
      setActionLoading(false);
    }
  };

  const sendPaymentReminder = async (orderId) => {
    try {
      setActionLoading(true);
      await api.post(`/admin/orders/${orderId}/send-payment-reminder`, {});
      alert('Payment reminder sent to customer!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    } finally {
      setActionLoading(false);
    }
  };

  const resendWarehousePush = async (orderId) => {
    try {
      setActionLoading(true);
      const res = await api.post(`/admin/orders/${orderId}/resend-warehouse-push`, {});
      const data = res?.data || {};
      const status = data.warehouse_push_status || 'unknown';
      if (data.ok) {
        toast.success(`Warehouse push sent · ${status}`);
      } else {
        toast.error(`Warehouse push failed · ${status}`);
      }
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => prev ? {
          ...prev,
          warehouse_push_status: data.warehouse_push_status,
          warehouse_pushed_at: data.warehouse_pushed_at,
        } : prev);
      }
      await loadData();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to resend warehouse push';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const shipOrder = async () => {
    if (!selectedOrder || !trackingNumber) return;
    
    try {
      setActionLoading(true);
      await api.post(`/admin/orders/${selectedOrder.id}/ship`, {
        order_id: selectedOrder.id,
        tracking_number: trackingNumber,
        carrier: carrier,
        estimated_delivery: '3-5 business days'
      });
      setShowShipModal(false);
      setTrackingNumber('');
      await loadData();
      alert('Order marked as shipped and customer notified!');
    } catch (error) {
      console.error('Error shipping order:', error);
      alert('Failed to ship order');
    } finally {
      setActionLoading(false);
    }
  };

  const markDelivered = async (orderId) => {
    try {
      setActionLoading(true);
      await api.post(`/admin/orders/${orderId}/delivered`);
      await loadData();
      alert('Order marked as delivered and customer notified!');
    } catch (error) {
      console.error('Error marking delivered:', error);
      alert('Failed to mark as delivered');
    } finally {
      setActionLoading(false);
    }
  };

  // Promo code actions
  const createPromoCode = async () => {
    if (!newPromo.code.trim()) {
      alert('Please enter a promo code');
      return;
    }
    
    try {
      setActionLoading(true);
      await api.post('/promo-codes', {
        code: newPromo.code.trim().toUpperCase(),
        discount_percent: parseFloat(newPromo.discount_percent) || 20,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        starts_at: newPromo.starts_at ? new Date(newPromo.starts_at).toISOString() : null,
        expires_at: newPromo.expires_at ? new Date(newPromo.expires_at).toISOString() : null,
        is_active: true
      });
      setShowPromoModal(false);
      setNewPromo({ code: '', discount_percent: 20, max_uses: null, starts_at: '', expires_at: '' });
      await loadData();
      alert('Promo code created successfully!');
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert(error.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setActionLoading(false);
    }
  };

  const updatePromoStatus = async (code, status) => {
    try {
      setActionLoading(true);
      await api.put(`/promo-codes/${code}`, { status });
      await loadData();
    } catch (error) {
      console.error('Error updating promo code:', error);
      alert('Failed to update promo code');
    } finally {
      setActionLoading(false);
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete promo code "${code}"?`)) return;
    
    try {
      setActionLoading(true);
      await api.delete(`/promo-codes/${code}`);
      await loadData();
      alert('Promo code deleted!');
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert('Failed to delete promo code');
    } finally {
      setActionLoading(false);
    }
  };

  const getPromoStatusBadge = (promo) => {
    const now = new Date();
    const startsAt = promo.starts_at ? new Date(promo.starts_at) : null;
    const expiresAt = promo.expires_at ? new Date(promo.expires_at) : null;
    
    // Check if expired
    if (expiresAt && now > expiresAt) {
      return { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired', icon: XCircle };
    }
    
    // Check if not started yet
    if (startsAt && now < startsAt) {
      return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled', icon: Calendar };
    }
    
    // Check status
    if (promo.status === 'paused') {
      return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused', icon: Pause };
    }
    
    if (promo.status === 'disabled' || !promo.is_active) {
      return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Disabled', icon: Ban };
    }
    
    // Active
    return { bg: 'bg-green-100', text: 'text-green-800', label: 'Active', icon: CheckCircle2 };
  };

  // Affiliate actions
  const [affiliateModal, setAffiliateModal] = useState(null);  // {action, affiliate}
  const [modalLang, setModalLang] = useState('en');
  const [modalReason, setModalReason] = useState('');

  const openAffiliateAction = (action, affiliate) => {
    setAffiliateModal({ action, affiliate });
    setModalLang(affiliate.preferred_language || 'en');
    setModalReason('');
  };

  const closeAffiliateModal = () => {
    setAffiliateModal(null);
    setModalReason('');
  };

  const confirmAffiliateAction = async () => {
    if (!affiliateModal) return;
    const { action, affiliate } = affiliateModal;
    try {
      setActionLoading(true);
      const body = { language: modalLang };
      if (action === 'reject' && modalReason.trim()) body.reason = modalReason.trim();
      await api.put(`/admin/affiliates/${affiliate.id}/${action}`, body);
      await loadData();
      closeAffiliateModal();
      alert(
        action === 'approve'
          ? `Affiliate approved! Email sent in ${modalLang.toUpperCase()}.`
          : `Affiliate rejected. Notification email sent in ${modalLang.toUpperCase()}.`
      );
    } catch (error) {
      console.error('Error updating affiliate:', error);
      alert(error.response?.data?.detail || 'Failed to update affiliate');
    } finally {
      setActionLoading(false);
    }
  };

  const approveAffiliate = (id) => {
    const aff = affiliates.find((a) => a.id === id);
    if (aff) openAffiliateAction('approve', aff);
  };

  const rejectAffiliate = (id) => {
    const aff = affiliates.find((a) => a.id === id);
    if (aff) openAffiliateAction('reject', aff);
  };

  const getAffiliateStatusBadge = (status) => {
    const configs = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
    };
    return configs[status] || configs.pending;
  };

  // Filter functions
  const filteredAffiliates = affiliates.filter(a => {
    const matchesFilter = affiliateFilter === 'all' || a.status === affiliateFilter;
    const matchesSearch = !searchTerm || 
      a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredUsers = users.filter(u => {
    const matchesFilter = userFilter === 'all' || u.verification_status === userFilter;
    const matchesSearch = !searchTerm || 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredOrders = orders.filter(o => {
    const matchesFilter = orderFilter === 'all' || o.status === orderFilter;
    const matchesSearch = !searchTerm ||
      o.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.shipping_address?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.shipping_address?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Helper functions
  const getStatusBadge = (status, type = 'user') => {
    const configs = {
      user: {
        under_review: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Under Review' },
        verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2, label: 'Verified' },
        not_approved: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
      },
      order: {
        pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
        pending_kyc: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertCircle, label: 'Pending KYC' },
        pending_kyc_approval: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertCircle, label: 'Pending KYC Approval' },
        kyc_approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2, label: 'KYC Approved' },
        awaiting_payment: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock, label: 'Awaiting Payment' },
        processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Package, label: 'Processing' },
        shipped: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Truck, label: 'Shipped' },
        delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2, label: 'Delivered' },
        cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: Ban, label: 'Cancelled' },
        returned: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Undo2, label: 'Returned' }
      },
      payment: {
        pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Unpaid' },
        paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' }
      }
    };
    
    const config = configs[type]?.[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {config.label}
      </span>
    );
  };

  const getUserOrderStats = (userId) => {
    const userOrders = orders.filter(o => o.user_id === userId);
    const totalSpent = userOrders
      .filter(o => o.payment_status === 'paid')
      .reduce((sum, o) => sum + (o.total || 0), 0);
    const lastOrder = userOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    
    return {
      orderCount: userOrders.length,
      totalSpent,
      lastOrderDate: lastOrder?.created_at
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Auth check
  if (!isAuthenticated) return null;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-capsule-bg to-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">You are not authorized to view this page</h1>
          <Button onClick={() => navigate('/')} className="bg-capsule-teal hover:bg-capsule-teal-dark text-white">
            Go Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Tab configurations
  const tabs = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      description: 'Overview & Statistics',
      color: 'from-capsule-teal to-rose-500'
    },
    { 
      id: 'users', 
      label: 'Users', 
      icon: UserCog,
      description: 'Manage Clients',
      color: 'from-blue-500 to-indigo-500',
      badge: stats.pendingUsers
    },
    { 
      id: 'orders', 
      label: 'Orders', 
      icon: ClipboardList,
      description: 'Manage Orders',
      color: 'from-green-500 to-emerald-500',
      badge: stats.pendingOrders
    },
    { 
      id: 'affiliates', 
      label: 'Affiliates', 
      icon: Users,
      description: 'Manage Partners',
      color: 'from-orange-500 to-amber-500',
      badge: affiliates.filter(a => a.status === 'pending').length
    },
    { 
      id: 'promo', 
      label: 'Promo Codes', 
      icon: Tag,
      description: 'Manage Discounts',
      color: 'from-purple-500 to-violet-500',
      badge: promoCodes.filter(p => p.status === 'active' || p.is_active).length
    },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: Star,
      description: 'Moderate customer reviews',
      color: 'from-yellow-500 to-amber-500',
      badge: stats.pendingReviews
    }
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Admin Header - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark text-white">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="adminGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#adminGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#adminGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-10 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-5 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-3 border border-white/20">
                <LayoutDashboard className="w-4 h-4 text-capsule-aqua" />
                <span className="text-sm font-medium text-capsule-aqua">Administration</span>
              </div>
              <h1 className="text-2xl font-bold">Admin Panel</h1>
              <p className="text-gray-400 text-sm">PenLab Peptide Business Management</p>
            </div>
            <Button 
              onClick={loadData} 
              variant="outline" 
              className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-xl" 
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
        </div>
      </div>

      {/* Main Navigation Tabs, horizontally scrollable on mobile/tablet so
          all 7+ tabs remain reachable without squeezing labels into 2 lines. */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div
            className="flex flex-nowrap overflow-x-auto nav-scroll-x"
            data-testid="admin-tabs-scroll"
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setSelectedUser(null); setSelectedOrder(null); }}
                className={`relative flex-shrink-0 min-w-[7rem] sm:min-w-[8.5rem] lg:flex-1 py-3 sm:py-4 px-3 sm:px-6 text-center transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'border-capsule-teal bg-capsule-teal/5' 
                    : 'border-transparent hover:bg-gray-50'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-2 ${
                  activeTab === tab.id 
                    ? `bg-gradient-to-br ${tab.color} text-white shadow-lg` 
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  <tab.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className={`font-semibold ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-600'}`}>
                    {tab.label}
                  </p>
                  <p className="text-xs text-gray-400">{tab.description}</p>
                </div>
                {tab.badge > 0 && (
                  <span className="absolute top-3 right-1/4 bg-capsule-salmon text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick links row */}
        <div className="flex items-center justify-end gap-2 mb-4">
          <a
            href="/admin/seo"
            data-testid="admin-seo-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white text-sm font-medium hover:opacity-90 transition shadow-sm"
          >
            SEO &amp; Rich Results
          </a>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-capsule-teal">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-capsule-teal/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-capsule-teal" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-amber-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Pending KYC</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.pendingUsers}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Orders</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Revenue</p>
                    <p className="text-2xl font-bold text-green-600">€{stats.totalRevenue.toFixed(0)}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-orange-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
                    <p className="text-2xl font-bold text-orange-600">€{stats.pendingRevenue.toFixed(0)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Pending Users */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold flex items-center gap-2">
                      <UserCheck className="w-5 h-5" />
                      Users Awaiting Approval
                    </h3>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                      {stats.pendingUsers}
                    </span>
                  </div>
                </div>
                <div className="divide-y max-h-80 overflow-y-auto">
                  {users.filter(u => u.verification_status === 'under_review').slice(0, 5).map(u => (
                    <div key={u.user_id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{u.name || 'No Name'}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <Building2 className="w-3 h-3" />
                            {u.organization_name || 'No organization'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600 text-white h-9 px-3"
                            onClick={() => updateUserStatus(u.user_id, 'verified')}
                            disabled={actionLoading}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-500 border-red-200 hover:bg-red-50 h-9 px-3"
                            onClick={() => updateUserStatus(u.user_id, 'not_approved')}
                            disabled={actionLoading}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {stats.pendingUsers === 0 && (
                    <div className="p-8 text-center text-gray-400">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />
                      <p className="font-medium">All caught up!</p>
                      <p className="text-sm">No pending approvals</p>
                    </div>
                  )}
                </div>
                {stats.pendingUsers > 5 && (
                  <div className="p-3 bg-gray-50 border-t">
                    <button 
                      onClick={() => { setActiveTab('users'); setUserFilter('under_review'); }}
                      className="text-sm text-capsule-teal hover:text-capsule-teal-dark font-medium w-full text-center"
                    >
                      View All {stats.pendingUsers} Pending Users →
                    </button>
                  </div>
                )}
              </div>

              {/* Pending Orders */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Orders Requiring Action
                    </h3>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                      {stats.pendingOrders}
                    </span>
                  </div>
                </div>
                <div className="divide-y max-h-80 overflow-y-auto">
                  {orders.filter(o => ['pending', 'pending_kyc', 'pending_kyc_approval', 'kyc_approved', 'awaiting_payment', 'processing'].includes(o.status)).slice(0, 5).map(o => (
                    <div key={o.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{o.id}</p>
                          <p className="text-sm text-gray-500">{o.shipping_address?.full_name}</p>
                          <div className="flex gap-2 mt-2">
                            {getStatusBadge(o.status, 'order')}
                            {getStatusBadge(o.payment_status || 'pending', 'payment')}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-capsule-teal">€{o.total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-400">{formatDate(o.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {stats.pendingOrders === 0 && (
                    <div className="p-8 text-center text-gray-400">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />
                      <p className="font-medium">All caught up!</p>
                      <p className="text-sm">No orders require action</p>
                    </div>
                  )}
                </div>
                {stats.pendingOrders > 5 && (
                  <div className="p-3 bg-gray-50 border-t">
                    <button 
                      onClick={() => { setActiveTab('orders'); setOrderFilter('pending_kyc_approval'); }}
                      className="text-sm text-capsule-teal hover:text-capsule-teal-dark font-medium w-full text-center"
                    >
                      View All Pending Orders →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
                <p className="text-green-100 text-sm">Verified Users</p>
                <p className="text-3xl font-bold mt-1">{stats.verifiedUsers}</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-5 text-white">
                <p className="text-red-100 text-sm">Rejected Users</p>
                <p className="text-3xl font-bold mt-1">{stats.rejectedUsers}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-5 text-white">
                <p className="text-purple-100 text-sm">Shipped Orders</p>
                <p className="text-3xl font-bold mt-1">{stats.shippedOrders}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 text-white">
                <p className="text-blue-100 text-sm">Paid Orders</p>
                <p className="text-3xl font-bold mt-1">{stats.paidOrders}</p>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Users List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <UserCog className="w-5 h-5" />
                    User Management
                  </h3>
                  <p className="text-blue-100 text-sm">View and manage all registered clients</p>
                </div>
                
                {/* Filters */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or organization..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <select 
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      className="px-4 py-2.5 border rounded-lg text-sm bg-white font-medium"
                    >
                      <option value="all">All Status ({users.length})</option>
                      <option value="under_review">⏳ Under Review ({stats.pendingUsers})</option>
                      <option value="verified">✅ Verified ({stats.verifiedUsers})</option>
                      <option value="not_approved">❌ Rejected ({stats.rejectedUsers})</option>
                    </select>
                  </div>
                </div>
                
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Orders</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Spent</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Registered</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-12 text-center">
                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
                            <p className="text-gray-500 mt-2">Loading users...</p>
                          </td>
                        </tr>
                      ) : filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-12 text-center text-gray-500">
                            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p>No users found</p>
                          </td>
                        </tr>
                      ) : filteredUsers.map(u => {
                        const orderStats = getUserOrderStats(u.user_id);
                        return (
                          <tr 
                            key={u.user_id} 
                            className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedUser?.user_id === u.user_id ? 'bg-blue-100' : ''}`}
                            onClick={() => setSelectedUser(u)}
                          >
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900">{u.name || '-'}</p>
                                <p className="text-xs text-gray-500">{u.email}</p>
                                {u.organization_name && (
                                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                    <Building2 className="w-3 h-3" />
                                    {u.organization_name}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(u.verification_status || 'under_review', 'user')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-medium">
                              {orderStats.orderCount}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">
                              €{orderStats.totalSpent.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {formatDate(u.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-3 bg-gray-50 border-t text-center text-sm text-gray-500">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
              </div>
            </div>

            {/* User Details Panel */}
            <div className="lg:col-span-1">
              {selectedUser ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden sticky top-36">
                  <div className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <h3 className="font-bold">User Details</h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-gray-900 text-lg">{selectedUser.name || 'No Name'}</p>
                        <p className="text-sm text-gray-500">{selectedUser.email}</p>
                      </div>
                      {getStatusBadge(selectedUser.verification_status || 'under_review', 'user')}
                    </div>
                    
                    {selectedUser.organization_name && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                          <Building2 className="w-3 h-3" /> Organization
                        </p>
                        <p className="font-medium text-gray-900">{selectedUser.organization_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{selectedUser.organization_type}</p>
                      </div>
                    )}
                    
                    {selectedUser.license_number && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">License Number</p>
                        <p className="font-mono font-medium text-gray-900">{selectedUser.license_number}</p>
                      </div>
                    )}
                    
                    {/* Order Stats */}
                    {(() => {
                      const stats = getUserOrderStats(selectedUser.user_id);
                      return (
                        <div className="bg-gradient-to-br from-capsule-teal/10 to-capsule-aqua/10 rounded-lg p-4 border border-capsule-teal/20">
                          <p className="text-xs text-capsule-teal font-medium mb-3">ORDER STATISTICS</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-2xl font-bold text-gray-900">{stats.orderCount}</p>
                              <p className="text-xs text-gray-500">Total Orders</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-capsule-teal">€{stats.totalSpent.toFixed(0)}</p>
                              <p className="text-xs text-gray-500">Total Spent</p>
                            </div>
                          </div>
                          {stats.lastOrderDate && (
                            <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Last order: {formatDate(stats.lastOrderDate)}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Registered</p>
                      <p className="text-sm text-gray-600">{formatDate(selectedUser.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="p-5 bg-gray-50 border-t space-y-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Actions</p>
                    <div className="grid gap-2">
                      <Button
                        className="w-full bg-green-500 hover:bg-green-600 text-white justify-center gap-2"
                        onClick={() => updateUserStatus(selectedUser.user_id, 'verified')}
                        disabled={actionLoading || selectedUser.verification_status === 'verified'}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve User
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full text-red-500 border-red-200 hover:bg-red-50 justify-center gap-2"
                        onClick={() => updateUserStatus(selectedUser.user_id, 'not_approved')}
                        disabled={actionLoading || selectedUser.verification_status === 'not_approved'}
                      >
                        <XCircle className="w-4 h-4" />
                        Reject User
                      </Button>
                      {selectedUser.verification_status !== 'under_review' && (
                        <Button
                          variant="outline"
                          className="w-full justify-center gap-2"
                          onClick={() => updateUserStatus(selectedUser.user_id, 'under_review')}
                          disabled={actionLoading}
                        >
                          <Clock className="w-4 h-4" />
                          Set Under Review
                        </Button>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full justify-center gap-2 mt-3"
                      onClick={() => window.open(`mailto:${selectedUser.email}`)}
                    >
                      <Mail className="w-4 h-4" />
                      Send Email
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Select a user</p>
                  <p className="text-sm">Click on a user to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Orders List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" />
                    Order Management
                  </h3>
                  <p className="text-green-100 text-sm">Track and manage all customer orders</p>
                </div>
                
                {/* Filters */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by order ID, customer name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <select 
                      value={orderFilter}
                      onChange={(e) => setOrderFilter(e.target.value)}
                      className="px-4 py-2.5 border rounded-lg text-sm bg-white font-medium"
                    >
                      <option value="all">All Status ({orders.length})</option>
                      <option value="pending">⏳ Pending</option>
                      <option value="pending_kyc_approval">📋 Pending KYC Approval</option>
                      <option value="kyc_approved">✅ KYC Approved</option>
                      <option value="awaiting_payment">💳 Awaiting Payment</option>
                      <option value="processing">⚙️ Processing</option>
                      <option value="shipped">🚚 Shipped</option>
                      <option value="delivered">✅ Delivered</option>
                      <option value="cancelled">❌ Cancelled</option>
                      <option value="returned">↩️ Returned</option>
                    </select>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/export.csv?status=${encodeURIComponent(orderFilter || 'all')}&q=${encodeURIComponent(searchTerm || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="orders-export-csv"
                      className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium bg-white hover:bg-gray-50 text-gray-700"
                    >
                      <DollarSign className="w-4 h-4" />
                      Export CSV
                    </a>
                  </div>
                </div>
                
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <RefreshCw className="w-8 h-8 text-green-500 animate-spin mx-auto" />
                            <p className="text-gray-500 mt-2">Loading orders...</p>
                          </td>
                        </tr>
                      ) : filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                            <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p>No orders found</p>
                          </td>
                        </tr>
                      ) : filteredOrders.map(o => (
                        <tr 
                          key={o.id} 
                          className={`hover:bg-green-50 cursor-pointer transition-colors ${selectedOrder?.id === o.id ? 'bg-green-100' : ''}`}
                          onClick={() => setSelectedOrder(o)}
                        >
                          <td className="px-4 py-3">
                            <p className="font-mono font-medium text-gray-900 text-sm">{o.id}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900">{o.shipping_address?.full_name}</p>
                            <p className="text-xs text-gray-500">{o.shipping_address?.organization_name}</p>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(o.status, 'order')}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(o.payment_status || 'pending', 'payment')}
                          </td>
                          <td className="px-4 py-3 font-bold text-capsule-teal">
                            €{o.total?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(o.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-3 bg-gray-50 border-t text-center text-sm text-gray-500">
                  Showing {filteredOrders.length} of {orders.length} orders
                </div>
              </div>
            </div>

            {/* Order Details Panel */}
            <div className="lg:col-span-1">
              {selectedOrder ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden sticky top-36">
                  <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                    <div className="flex justify-between items-center">
                      <h3 className="font-bold">Order Details</h3>
                      <p className="text-xl font-bold">€{selectedOrder.total?.toFixed(2)}</p>
                    </div>
                    <p className="text-green-100 text-sm font-mono">{selectedOrder.id}</p>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      {getStatusBadge(selectedOrder.status, 'order')}
                      {getStatusBadge(selectedOrder.payment_status || 'pending', 'payment')}
                    </div>
                    
                    {/* Customer */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                        <Users className="w-3 h-3" /> Customer
                      </p>
                      <p className="font-medium text-gray-900">{selectedOrder.shipping_address?.full_name}</p>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.email}</p>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.phone}</p>
                    </div>
                    
                    {/* Organization */}
                    {selectedOrder.shipping_address?.organization_name && (
                      <div>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                          <Building2 className="w-3 h-3" /> Organization
                        </p>
                        <p className="font-medium text-gray-900">{selectedOrder.shipping_address?.organization_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{selectedOrder.shipping_address?.organization_type}</p>
                      </div>
                    )}
                    
                    {/* Address */}
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                        <MapPin className="w-3 h-3" /> Shipping Address
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedOrder.shipping_address?.address_line1}<br />
                        {selectedOrder.shipping_address?.address_line2 && <>{selectedOrder.shipping_address.address_line2}<br /></>}
                        {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.postal_code}<br />
                        {selectedOrder.shipping_address?.country}
                      </p>
                    </div>
                    
                    {/* Tracking */}
                    {selectedOrder.tracking_number && (
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                        <p className="text-xs text-purple-600 font-medium mb-1">TRACKING</p>
                        <p className="font-mono font-bold text-purple-800">{selectedOrder.tracking_number}</p>
                        <p className="text-xs text-purple-600">{selectedOrder.carrier || 'DHL Express'}</p>
                      </div>
                    )}
                    
                    {/* Promo / Affiliate */}
                    {(selectedOrder.promo_code || selectedOrder.affiliate_id) && (
                      <div data-testid="order-promo-affiliate-block">
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                          <DollarSign className="w-3 h-3" /> Promotion / Affiliate
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedOrder.promo_code && (
                            <span
                              data-testid="order-promo-code-badge"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-orange-100 text-orange-800 text-xs font-mono font-semibold border border-orange-200"
                            >
                              PROMO · {selectedOrder.promo_code}
                            </span>
                          )}
                          {selectedOrder.affiliate_id && (
                            <span
                              data-testid="order-affiliate-id-badge"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 text-purple-800 text-xs font-mono font-semibold border border-purple-200"
                            >
                              AFFILIATE · {String(selectedOrder.affiliate_id).slice(0, 12)}
                            </span>
                          )}
                          {typeof selectedOrder.discount_amount === 'number' && selectedOrder.discount_amount > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-800 text-xs font-semibold border border-green-200">
                              −€{selectedOrder.discount_amount.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Items */}
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                        <Package className="w-3 h-3" /> Items ({selectedOrder.items?.length || 0})
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="order-items-list">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-start text-sm bg-gray-50 p-2 rounded gap-2" data-testid={`order-item-${idx}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-800 truncate font-medium">
                                {item.product_name?.en || item.product_name?.es}
                                <span className="text-gray-400 font-normal"> · x{item.quantity}</span>
                              </div>
                              <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px]">
                                {item.sku && (
                                  <span data-testid={`order-item-sku-${idx}`} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono">
                                    SKU · {item.sku}
                                  </span>
                                )}
                                {item.selected_variant && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">
                                    {item.selected_variant}
                                  </span>
                                )}
                                {!item.sku && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 italic">
                                    no sku
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="font-medium whitespace-nowrap">€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Warehouse / 3PL push status */}
                  {selectedOrder.payment_status === 'paid' && (
                    <div className="px-5 pb-3" data-testid="order-warehouse-block">
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                        <Truck className="w-3 h-3" /> 3PL / Warehouse push
                      </p>
                      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            data-testid="warehouse-push-status"
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold ${
                              selectedOrder.warehouse_push_status === 'ok'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : selectedOrder.warehouse_push_status
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-gray-200 text-gray-600 border border-gray-300'
                            }`}
                          >
                            {selectedOrder.warehouse_push_status || 'not pushed'}
                          </span>
                          {selectedOrder.warehouse_pushed_at && (
                            <span className="text-[11px] text-gray-500 truncate">
                              {new Date(selectedOrder.warehouse_pushed_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          className="h-7 px-2 text-xs gap-1"
                          onClick={() => resendWarehousePush(selectedOrder.id)}
                          disabled={actionLoading}
                          data-testid="resend-warehouse-push-button"
                          title="Re-fire the outbound 3PL webhook for this order"
                        >
                          <Send className="w-3 h-3" />
                          Resend
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="p-5 bg-gray-50 border-t space-y-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Quick Actions</p>
                    
                    {/* Payment Actions */}
                    {selectedOrder.payment_status !== 'paid' && selectedOrder.status !== 'cancelled' && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="bg-green-500 hover:bg-green-600 text-white justify-center gap-1 text-sm"
                          onClick={() => markPaymentReceived(selectedOrder.id)}
                          disabled={actionLoading}
                        >
                          <DollarSign className="w-4 h-4" />
                          Paid
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-center gap-1 text-sm"
                          onClick={() => sendPaymentReminder(selectedOrder.id)}
                          disabled={actionLoading}
                        >
                          <Send className="w-4 h-4" />
                          Remind
                        </Button>
                      </div>
                    )}
                    
                    {/* Order Actions */}
                    <div className="grid grid-cols-2 gap-2">
                      {selectedOrder.status !== 'shipped' && selectedOrder.payment_status === 'paid' && (
                        <Button
                          className="bg-purple-500 hover:bg-purple-600 text-white justify-center gap-1 text-sm"
                          onClick={() => setShowShipModal(true)}
                          disabled={actionLoading}
                        >
                          <Truck className="w-4 h-4" />
                          Ship
                        </Button>
                      )}
                      
                      {selectedOrder.status === 'shipped' && (
                        <Button
                          className="bg-green-500 hover:bg-green-600 text-white justify-center gap-1 text-sm"
                          onClick={() => markDelivered(selectedOrder.id)}
                          disabled={actionLoading}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Delivered
                        </Button>
                      )}
                      
                      {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'delivered' && (
                        <Button
                          variant="outline"
                          className="text-red-500 border-red-200 hover:bg-red-50 justify-center gap-1 text-sm"
                          onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                          disabled={actionLoading}
                        >
                          <Ban className="w-4 h-4" />
                          Cancel
                        </Button>
                      )}
                      
                      {selectedOrder.status === 'delivered' && (
                        <Button
                          variant="outline"
                          className="justify-center gap-1 text-sm"
                          onClick={() => updateOrderStatus(selectedOrder.id, 'returned')}
                          disabled={actionLoading}
                        >
                          <Undo2 className="w-4 h-4" />
                          Returned
                        </Button>
                      )}
                    </div>
                    
                    {/* Manual Status */}
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Manual Status Update</p>
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        disabled={actionLoading}
                      >
                        <option value="pending">Pending</option>
                        <option value="pending_kyc_approval">Pending KYC Approval</option>
                        <option value="kyc_approved">KYC Approved</option>
                        <option value="awaiting_payment">Awaiting Payment</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="returned">Returned</option>
                      </select>
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full justify-center gap-2"
                      onClick={() => window.open(`mailto:${selectedOrder.shipping_address?.email}`)}
                    >
                      <Mail className="w-4 h-4" />
                      Email Customer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Select an order</p>
                  <p className="text-sm">Click on an order to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Ship Order Modal */}
      {showShipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Ship Order
              </h3>
              <p className="text-purple-100 text-sm">{selectedOrder?.id}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tracking Number *</label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Carrier</label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                >
                  <option value="DHL Express">DHL Express</option>
                  <option value="FedEx">FedEx</option>
                  <option value="UPS">UPS</option>
                  <option value="Correos">Correos</option>
                  <option value="GLS">GLS</option>
                  <option value="SEUR">SEUR</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700 flex items-start gap-2">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>Customer will automatically receive an email with the tracking information.</p>
              </div>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowShipModal(false); setTrackingNumber(''); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                onClick={shipOrder}
                disabled={!trackingNumber || actionLoading}
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Confirm Shipment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Affiliates Tab */}
      {activeTab === 'affiliates' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Affiliate Applications</h2>
              <p className="text-gray-500 text-sm">Review and manage affiliate partner applications</p>
            </div>
            <div className="flex gap-2">
              <select
                value={affiliateFilter}
                onChange={(e) => setAffiliateFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{affiliates.filter(a => a.status === 'pending').length}</p>
                  <p className="text-xs text-gray-500">Pending Review</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{affiliates.filter(a => a.status === 'approved').length}</p>
                  <p className="text-xs text-gray-500">Approved</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{affiliates.filter(a => a.status === 'rejected').length}</p>
                  <p className="text-xs text-gray-500">Rejected</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{affiliates.length}</p>
                  <p className="text-xs text-gray-500">Total Applications</p>
                </div>
              </div>
            </div>
          </div>

          {/* Affiliates Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Applicant</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Social Media</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Applied</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAffiliates.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No affiliate applications yet</p>
                        <p className="text-sm">Applications will appear here when users apply</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAffiliates.map(affiliate => {
                      const statusBadge = getAffiliateStatusBadge(affiliate.status);
                      const StatusIcon = statusBadge.icon;
                      
                      return (
                        <tr key={affiliate.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-gray-900">{affiliate.name}</p>
                              {affiliate.message && (
                                <p className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={affiliate.message}>
                                  "{affiliate.message}"
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Mail className="w-3.5 h-3.5" />
                                <span>{affiliate.email}</span>
                              </div>
                              {affiliate.phone && (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>{affiliate.phone}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {affiliate.socials?.instagram && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-50 text-pink-700 rounded text-xs">
                                  IG
                                </span>
                              )}
                              {affiliate.socials?.tiktok && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                  TikTok
                                </span>
                              )}
                              {affiliate.socials?.youtube && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">
                                  YT
                                </span>
                              )}
                              {affiliate.socials?.twitter && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                  X
                                </span>
                              )}
                              {affiliate.socials?.facebook && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                  FB
                                </span>
                              )}
                              {!affiliate.socials?.instagram && !affiliate.socials?.tiktok && !affiliate.socials?.youtube && !affiliate.socials?.twitter && !affiliate.socials?.facebook && (
                                <span className="text-gray-400 text-xs">None provided</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              <StatusIcon className="w-3.5 h-3.5" />
                              {statusBadge.label}
                            </span>
                            {affiliate.promo_code && (
                              <p className="mt-1 text-xs font-mono text-orange-600">{affiliate.promo_code}</p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(affiliate.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            {affiliate.status === 'pending' ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => approveAffiliate(affiliate.id)}
                                  disabled={actionLoading}
                                  className="bg-green-500 hover:bg-green-600 text-white text-xs px-3"
                                >
                                  <Check className="w-3.5 h-3.5 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => rejectAffiliate(affiliate.id)}
                                  disabled={actionLoading}
                                  className="text-red-600 border-red-300 hover:bg-red-50 text-xs px-3"
                                >
                                  <XCircle className="w-3.5 h-3.5 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedAffiliate(affiliate)}
                                className="text-gray-600 text-xs"
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                View Details
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Details Modal */}
      {selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Affiliate Details</h3>
                <button onClick={() => setSelectedAffiliate(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Name</label>
                <p className="text-gray-900 font-medium">{selectedAffiliate.name}</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-500 uppercase">Email</label>
                <p className="text-gray-900">{selectedAffiliate.email}</p>
              </div>
              
              {selectedAffiliate.phone && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Phone</label>
                  <p className="text-gray-900">{selectedAffiliate.phone}</p>
                </div>
              )}
              
              <div>
                <label className="text-xs text-gray-500 uppercase">Status</label>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getAffiliateStatusBadge(selectedAffiliate.status).bg} ${getAffiliateStatusBadge(selectedAffiliate.status).text}`}>
                  {selectedAffiliate.status}
                </span>
              </div>
              
              {selectedAffiliate.promo_code && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Promo Code</label>
                  <p className="font-mono text-lg text-orange-600 font-bold">{selectedAffiliate.promo_code}</p>
                </div>
              )}
              
              {selectedAffiliate.message && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Message</label>
                  <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded-lg">{selectedAffiliate.message}</p>
                </div>
              )}
              
              <div>
                <label className="text-xs text-gray-500 uppercase">Social Media</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedAffiliate.socials?.instagram && (
                    <a href={`https://instagram.com/${selectedAffiliate.socials.instagram}`} target="_blank" rel="noopener noreferrer" className="text-pink-600 text-sm hover:underline">
                      @{selectedAffiliate.socials.instagram}
                    </a>
                  )}
                  {selectedAffiliate.socials?.tiktok && (
                    <span className="text-gray-700 text-sm">TikTok: {selectedAffiliate.socials.tiktok}</span>
                  )}
                  {selectedAffiliate.socials?.youtube && (
                    <span className="text-red-600 text-sm">YouTube: {selectedAffiliate.socials.youtube}</span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-xs text-gray-500 uppercase">Applied On</label>
                <p className="text-gray-900">{formatDate(selectedAffiliate.created_at)}</p>
              </div>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 border-t rounded-b-2xl">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setSelectedAffiliate(null)}
              >
                Close
              </Button>
              {selectedAffiliate.status === 'pending' && (
                <>
                  <Button
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => { approveAffiliate(selectedAffiliate.id); setSelectedAffiliate(null); }}
                    disabled={actionLoading}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => { rejectAffiliate(selectedAffiliate.id); setSelectedAffiliate(null); }}
                    disabled={actionLoading}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Promo Codes Tab */}
      {activeTab === 'promo' && (
        <div className="space-y-6">
          {/* Header with Create Button */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Promo Codes</h2>
              <p className="text-gray-500 text-sm">Manage discount codes for customers and affiliates</p>
            </div>
            <Button
              onClick={() => setShowPromoModal(true)}
              className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Promo Code
            </Button>
          </div>

          {/* Promo Codes Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Discount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Active Period</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Usage</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {promoCodes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        <Tag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No promo codes yet</p>
                        <p className="text-sm">Create your first promo code to get started</p>
                      </td>
                    </tr>
                  ) : (
                    promoCodes.map(promo => {
                      const statusBadge = getPromoStatusBadge(promo);
                      const StatusIcon = statusBadge.icon;
                      
                      return (
                        <tr key={promo.code} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-lg">
                                {promo.code}
                              </span>
                              {promo.affiliate_id && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                  Affiliate
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <Percent className="w-4 h-4 text-green-600" />
                              <span className="font-semibold text-green-600">{promo.discount_percent}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusBadge.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div className="space-y-1">
                              {promo.starts_at && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">From:</span>
                                  <span>{formatDate(promo.starts_at)}</span>
                                </div>
                              )}
                              {promo.expires_at && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">To:</span>
                                  <span>{formatDate(promo.expires_at)}</span>
                                </div>
                              )}
                              {!promo.starts_at && !promo.expires_at && (
                                <span className="text-gray-400">No limit</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <span className="font-medium">{promo.current_uses || 0}</span>
                              <span className="text-gray-400">
                                {promo.max_uses ? ` / ${promo.max_uses}` : ' uses'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {promo.status === 'active' || promo.is_active ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updatePromoStatus(promo.code, 'paused')}
                                  disabled={actionLoading}
                                  className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                  title="Pause"
                                >
                                  <Pause className="w-4 h-4" />
                                </Button>
                              ) : promo.status === 'paused' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updatePromoStatus(promo.code, 'active')}
                                  disabled={actionLoading}
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                  title="Activate"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updatePromoStatus(promo.code, 'active')}
                                  disabled={actionLoading}
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                  title="Enable"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updatePromoStatus(promo.code, 'disabled')}
                                disabled={actionLoading || promo.status === 'disabled'}
                                className="text-gray-600 border-gray-300 hover:bg-gray-50"
                                title="Disable"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                              {!promo.affiliate_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deletePromoCode(promo.code)}
                                  disabled={actionLoading}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create Promo Code Modal */}
      {showPromoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-500" />
                Create Promo Code
              </h3>
              <p className="text-sm text-gray-500 mt-1">Set up a new discount code for your customers</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Promo Code *</label>
                <input
                  type="text"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SUMMER20"
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono uppercase"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount Percentage *</label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newPromo.discount_percent}
                    onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Uses (optional)</label>
                <input
                  type="number"
                  min="1"
                  value={newPromo.max_uses || ''}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={newPromo.starts_at}
                    onChange={(e) => setNewPromo({ ...newPromo, starts_at: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={newPromo.expires_at}
                    onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 text-sm text-purple-700 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>The promo code will be active immediately unless you set a start date. Customers can use this code at checkout.</p>
              </div>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 border-t rounded-b-2xl">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowPromoModal(false); setNewPromo({ code: '', discount_percent: 20, max_uses: null, starts_at: '', expires_at: '' }); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white"
                onClick={createPromoCode}
                disabled={!newPromo.code.trim() || actionLoading}
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Create Code'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate approve / reject modal */}
      {affiliateModal && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="affiliate-action-modal"
          onClick={closeAffiliateModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  affiliateModal.action === 'approve'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {affiliateModal.action === 'approve' ? (
                  <CheckCircle2 className="w-6 h-6" />
                ) : (
                  <XCircle className="w-6 h-6" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {affiliateModal.action === 'approve'
                    ? 'Approve affiliate'
                    : 'Reject affiliate'}
                </h3>
                <p className="text-xs text-gray-500">
                  {affiliateModal.affiliate.name} · {affiliateModal.affiliate.email}
                </p>
              </div>
            </div>

            {affiliateModal.action === 'approve' ? (
              <p className="text-sm text-gray-700 mb-4">
                A unique promo code will be generated and sent to this affiliate
                via email along with a one-time setup link for their dashboard
                password. The standardised legal disclaimer is appended automatically.
              </p>
            ) : (
              <p className="text-sm text-gray-700 mb-4">
                The applicant will receive a polite rejection email in the
                language you select below. The standardised legal disclaimer is
                appended automatically.
              </p>
            )}

            <label className="block text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Email language
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { code: 'en', label: '🇬🇧 English' },
                { code: 'es', label: '🇪🇸 Español' },
                { code: 'ru', label: '🇷🇺 Русский' },
              ].map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => setModalLang(opt.code)}
                  data-testid={`modal-lang-${opt.code}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition ${
                    modalLang === opt.code
                      ? 'border-capsule-teal bg-capsule-teal/10 text-capsule-teal'
                      : 'border-gray-200 text-gray-600 hover:border-capsule-teal/40'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {affiliateModal.action === 'reject' && (
              <>
                <label className="block text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                  Reason (optional, will appear in the email)
                </label>
                <textarea
                  value={modalReason}
                  onChange={(e) => setModalReason(e.target.value)}
                  rows={3}
                  data-testid="modal-reject-reason"
                  className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-capsule-teal focus:ring-2 focus:ring-capsule-teal/20 outline-none mb-4"
                  placeholder="e.g. Not enough audience overlap with research clientele right now."
                />
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeAffiliateModal}
                data-testid="modal-cancel"
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmAffiliateAction}
                disabled={actionLoading}
                data-testid="modal-confirm"
                className={
                  affiliateModal.action === 'approve'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                    : 'bg-gradient-to-r from-red-500 to-rose-500 text-white'
                }
              >
                {actionLoading
                  ? 'Sending...'
                  : affiliateModal.action === 'approve'
                  ? 'Approve & send email'
                  : 'Reject & send email'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ============= REVIEWS MODERATION TAB ============= */}
      {activeTab === 'reviews' && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-reviews-tab">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="p-5 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Customer Reviews
                </h2>
                <p className="text-sm text-gray-500">
                  Verified-purchase reviews require admin approval before going live on product pages.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {['pending', 'approved', 'rejected', 'all'].map(f => (
                  <button
                    key={f}
                    onClick={() => setReviewFilter(f)}
                    className={`px-3 py-1.5 text-xs rounded-md font-semibold capitalize ${
                      reviewFilter === f
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`reviews-filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-5">
              {reviewLoading && (
                <div className="text-sm text-gray-500">Loading reviews...</div>
              )}
              {!reviewLoading && reviews.length === 0 && (
                <div className="text-sm text-gray-500" data-testid="reviews-empty">
                  No reviews matching this filter.
                </div>
              )}
              <ul className="divide-y divide-gray-100">
                {reviews.map(r => (
                  <li key={r.id} className="py-4 flex flex-col gap-2" data-testid={`admin-review-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                className={`w-4 h-4 ${n <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </span>
                          <h3 className="font-semibold text-gray-900 truncate">{r.title}</h3>
                          <span className={`text-[11px] px-1.5 py-0.5 rounded font-mono ${
                            r.status === 'approved' ? 'bg-green-100 text-green-700' :
                            r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {r.status}
                          </span>
                          {r.verified_purchase && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">verified</span>
                          )}
                          <span className="text-[11px] uppercase text-gray-400">{r.language}</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{r.text}</p>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="font-medium text-gray-700">{r.reviewer_name}</span>
                          <span> · </span>
                          <span>order {r.order_id}</span>
                          <span> · </span>
                          <span>product {r.product_id}</span>
                          <span> · </span>
                          <span>{new Date(r.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {r.status !== 'approved' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                            disabled={actionLoading}
                            onClick={() => moderateReview(r.id, 'approve')}
                            data-testid={`review-approve-${r.id}`}
                          >
                            <ThumbsUp className="w-3.5 h-3.5 mr-1" />
                            Approve
                          </Button>
                        )}
                        {r.status !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            disabled={actionLoading}
                            onClick={() => moderateReview(r.id, 'reject')}
                            data-testid={`review-reject-${r.id}`}
                          >
                            <ThumbsDown className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          disabled={actionLoading}
                          onClick={() => deleteReview(r.id)}
                          data-testid={`review-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </main>
      )}

      <Footer />
    </div>
  );
};

export default AdminPage;
